import React, { ReactNode } from 'react'
import { cx } from '../../lib/utils'

// Tremor-style Table components
const TableRoot = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className="overflow-x-auto">
      <div className={cx('w-full overflow-auto whitespace-nowrap', className)} {...props}>
        {children}
      </div>
    </div>
  ),
)
TableRoot.displayName = 'TableRoot'

const Table = React.forwardRef<HTMLTableElement, React.TableHTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <table
      ref={ref}
      className={cx(
        'w-full caption-bottom border-b',
        'border-gray-300 dark:border-gray-800',
        className,
      )}
      {...props}
    />
  ),
)
Table.displayName = 'Table'

const TableHead = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={className} {...props} />
))
TableHead.displayName = 'TableHead'

const TableHeaderCell = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cx(
      'whitespace-nowrap border-b px-4 py-3 text-left text-micro font-medium uppercase',
      'text-gray-500 dark:text-gray-400',
      'border-primary-600 dark:border-gray-300',
      className,
    )}
    {...props}
  />
))
TableHeaderCell.displayName = 'TableHeaderCell'

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cx('divide-y', 'divide-gray-300 dark:divide-gray-800', '[&>tr:nth-child(even)]:bg-gray-100 dark:[&>tr:nth-child(even)]:bg-gray-900/60', className)}
    {...props}
  />
))
TableBody.displayName = 'TableBody'

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cx(
        '[&_td:last-child]:pr-4 [&_th:last-child]:pr-4',
        '[&_td:first-child]:pl-4 [&_th:first-child]:pl-4',
        'sm:[&_td:last-child]:pr-6 sm:[&_th:last-child]:pr-6',
        'sm:[&_td:first-child]:pl-6 sm:[&_th:first-child]:pl-6',
        className,
      )}
      {...props}
    />
  ),
)
TableRow.displayName = 'TableRow'

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cx(
      'whitespace-nowrap p-4 text-sm',
      'text-primary-600 dark:text-gray-200',
      className,
    )}
    {...props}
  />
))
TableCell.displayName = 'TableCell'

const TableFoot = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cx(
      'border-t text-left font-medium',
      'text-gray-900 dark:text-gray-50',
      'border-gray-200 dark:border-gray-800',
      className,
    )}
    {...props}
  />
))
TableFoot.displayName = 'TableFoot'

// Table Row Group (for grouped tables like the Tremor template)
interface TableRowGroupProps extends React.HTMLAttributes<HTMLTableRowElement> {
  label: string
  count?: number
}

const TableRowGroup = React.forwardRef<HTMLTableRowElement, TableRowGroupProps>(
  ({ label, count, className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cx('bg-gray-50 dark:bg-gray-900', className)}
      {...props}
    >
      <td colSpan={100} className="py-3 pl-4 sm:pl-6">
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-50">
          {label}
        </span>
        {count !== undefined && (
          <span className="ml-2 font-medium text-gray-600 dark:text-gray-400">{count}</span>
        )}
      </td>
    </tr>
  ),
)
TableRowGroup.displayName = 'TableRowGroup'

// Legacy Table component for backward compatibility
interface Column<T> {
  key: string
  header: string
  render?: (item: T) => ReactNode
  className?: string
}

interface LegacyTableProps<T> {
  columns: Column<T>[]
  data: T[]
  keyExtractor: (item: T) => string
  onRowClick?: (item: T) => void
  emptyState?: ReactNode
  loading?: boolean
}

function LegacyTable<T>({
  columns,
  data,
  keyExtractor,
  onRowClick,
  emptyState,
  loading = false,
}: LegacyTableProps<T>) {
  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-[50%] h-8 w-8 border-b-2 border-primary-600 dark:border-gray-50 mx-auto"></div>
      </div>
    )
  }

  if (data.length === 0 && emptyState) {
    return <>{emptyState}</>
  }

  return (
    <TableRoot>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableHeaderCell key={column.key} className={column.className}>
                {column.header}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item) => (
            <TableRow
              key={keyExtractor(item)}
              onClick={() => onRowClick?.(item)}
              className={onRowClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors' : ''}
            >
              {columns.map((column) => (
                <TableCell key={column.key} className={column.className}>
                  {column.render
                    ? column.render(item)
                    : (item as Record<string, unknown>)[column.key] as ReactNode}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableRoot>
  )
}

// Simple list for mobile or compact views
interface StackedListProps<T> {
  data: T[]
  keyExtractor: (item: T) => string
  renderItem: (item: T) => ReactNode
  onItemClick?: (item: T) => void
  emptyState?: ReactNode
  loading?: boolean
}

function StackedList<T>({
  data,
  keyExtractor,
  renderItem,
  onItemClick,
  emptyState,
  loading = false,
}: StackedListProps<T>) {
  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-[50%] h-8 w-8 border-b-2 border-primary-600 dark:border-gray-50 mx-auto"></div>
      </div>
    )
  }

  if (data.length === 0 && emptyState) {
    return <>{emptyState}</>
  }

  return (
    <ul role="list" className="divide-y divide-gray-200 dark:divide-gray-800">
      {data.map((item) => (
        <li
          key={keyExtractor(item)}
          onClick={() => onItemClick?.(item)}
          className={onItemClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors' : ''}
        >
          {renderItem(item)}
        </li>
      ))}
    </ul>
  )
}

export default LegacyTable
export {
  Table,
  TableRoot,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableFoot,
  TableRowGroup,
  StackedList,
}
